import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from "../../../redux/modalSlice";
import { useForm, Controller } from 'react-hook-form';
import { useRequest } from '../../../hooks/UseRequest';
import { setNotification } from '../../../redux/notificationSlice';
import Loading from '../../../components/Loading';
import Select from 'react-select';
import { reactSelectClassNames } from '../../../helpers/react-select-styles';
import { selectDropdownOptions, selectDropdowns } from '../../../redux/dropdownSlice';
import SpecialInstruction from '../../../components/elements/SpecialInstruction';
import { format } from 'date-fns';

export default function RefinanceCashOutModal() {
  const dispatch = useDispatch();
  const modal = useSelector(selectModal);
  const dropdowns = useSelector(selectDropdowns);
  const dropdownOptions = useSelector(selectDropdownOptions);
  const { handleSubmit, control, formState: { errors }, reset, setValue } = useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [eligibilityResult, setEligibilityResult] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [customerBankAccounts, setCustomerBankAccounts] = useState([]);
  const [currentBankAccount, setCurrentBankAccount] = useState(null);
  const [customer, setCustomer] = useState({});
  const [payFrequencyValue, setPayFrequencyValue] = useState("");
  const [payFrequencyValueOption, setPayFrequencyValueOption] = useState("");
  const request = useRequest();
  const loanId = modal.options?.loanId;
  const customerId = modal.options?.customerId;
  const modalRef = useRef(null);
  const inFlightLoanId = useRef(null);

  // Pay frequency dropdown options
  const payFrequency = dropdowns.data.filter((o) => o.code_key === 'pay_frequency')[0];
  const payFrequencyOptions = dropdownOptions.filter((o) => o.dropdown_id === payFrequency.id && o.code_key !== 'pay_frequency_daily').map((o) => ({ label: o.name, value: o.id }));

  // Day of week dropdown options
  const dayOfWeek = dropdowns.data.filter((o) => o.code_key === 'day_of_week')[0];
  const dayOfWeekOptions = dropdownOptions.filter((o) => o.dropdown_id === dayOfWeek.id).map((o) => ({ label: o.name, value: o.id }));

  // Week of month dropdown options
  const weekOfMonth = dropdowns.data.filter((o) => o.code_key === 'week_of_month')[0];
  const weekOfMonthOptions = dropdownOptions.filter((o) => o.dropdown_id === weekOfMonth.id).map((o) => ({ label: o.name, value: o.id }));

  // Day of month dropdown options
  const dayOfMonth = dropdowns.data.filter((o) => o.code_key === 'day_of_month')[0];
  const dayOfMonthOptions = dropdownOptions.filter((o) => o.dropdown_id === dayOfMonth.id).map((o) => ({ label: o.name, value: o.id }));

  // This modal stays mounted in the Footer, so start every opening from a clean slate — whatever path
  // closed it last time — before checking this loan's eligibility.
  useEffect(() => {
    if (modal.is_active && modal.type === 'refinance_cash_out_modal' && loanId) {
      resetState();
      checkEligibility(loanId);
    } else {
      inFlightLoanId.current = null;
    }
  }, [modal.is_active, modal.type, loanId]);

  useEffect(() => {
    if (currentBankAccount) {
      setValue('customer_bank_account_id', currentBankAccount);
    }
  }, [currentBankAccount, setValue]);

  // Set pay frequency and related fields when customer data is loaded
  useEffect(() => {
    if (customer && Object.keys(customer).length > 0 && payFrequencyOptions.length > 0 && dayOfWeekOptions.length > 0) {
      // Set pay frequency if available and not already set
      if (customer.pay_frequency_dropdown_option_id && !payFrequencyValue) {
        const payFreqOption = payFrequencyOptions.find((option) => option.value === customer.pay_frequency_dropdown_option_id);
        if (payFreqOption) {
          setValue('pay_frequency_dropdown_option_id', payFreqOption);
          setPayFrequencyValue(payFreqOption.label);
        }
      }

      // Set pay day of week if available and not already set
      if (customer.pay_first_day_of_week_dropdown_option_id) {
        const payDayOption = dayOfWeekOptions.find((option) => option.value === customer.pay_first_day_of_week_dropdown_option_id);
        if (payDayOption) {
          setValue('pay_first_day_of_week_dropdown_option_id', payDayOption);
        }
      }

      // Set pay second day of week if available
      if (customer.pay_second_day_of_week_dropdown_option_id) {
        const paySecondDayOption = dayOfWeekOptions.find((option) => option.value === customer.pay_second_day_of_week_dropdown_option_id);
        if (paySecondDayOption) {
          setValue('pay_second_day_of_week_dropdown_option_id', paySecondDayOption);
        }
      }

      // Set pay first day of month if available
      if (customer.pay_first_day_of_month_dropdown_option_id) {
        const payFirstDayMonthOption = dayOfMonthOptions.find((option) => option.value === customer.pay_first_day_of_month_dropdown_option_id);
        if (payFirstDayMonthOption) {
          setValue('pay_first_day_of_month_dropdown_option_id', payFirstDayMonthOption);
        }
      }

      // Set pay second day of month if available
      if (customer.pay_second_day_of_month_dropdown_option_id) {
        const paySecondDayMonthOption = dayOfMonthOptions.find((option) => option.value === customer.pay_second_day_of_month_dropdown_option_id);
        if (paySecondDayMonthOption) {
          setValue('pay_second_day_of_month_dropdown_option_id', paySecondDayMonthOption);
        }
      }

      // Set pay first week of month if available
      if (customer.pay_first_week_of_month_dropdown_option_id) {
        const payFirstWeekOption = weekOfMonthOptions.find((option) => option.value === customer.pay_first_week_of_month_dropdown_option_id);
        if (payFirstWeekOption) {
          setValue('pay_first_week_of_month_dropdown_option_id', payFirstWeekOption);
        }
      }

      // Set pay second week of month if available
      if (customer.pay_second_week_of_month_dropdown_option_id) {
        const paySecondWeekOption = weekOfMonthOptions.find((option) => option.value === customer.pay_second_week_of_month_dropdown_option_id);
        if (paySecondWeekOption) {
          setValue('pay_second_week_of_month_dropdown_option_id', paySecondWeekOption);
        }
      }

      // Set next pay date if available
      if (customer.next_pay_date) {
        setValue('next_pay_date', customer.next_pay_date);
      }

      // Determine and set the pay frequency value option based on customer data
      if (customer.pay_frequency_dropdown_option_id && !payFrequencyValueOption) {
        const payFreqCodeKey = Object.values(dropdownOptions).find((option) => option.id === customer.pay_frequency_dropdown_option_id)?.code_key;

        if (payFreqCodeKey === 'pay_frequency_semimonthly') {
          // Determine if it's specific days or week-based
          if (customer.pay_first_day_of_month_dropdown_option_id && customer.pay_second_day_of_month_dropdown_option_id) {
            setPayFrequencyValueOption('semimonthly-option1');
            setValue('semi_monthly_pay_frequency', { label: 'Specific Days', value: 'semimonthly-option1' });
          } else if (customer.pay_first_week_of_month_dropdown_option_id && customer.pay_second_week_of_month_dropdown_option_id) {
            setPayFrequencyValueOption('semimonthly-option2');
            setValue('semi_monthly_pay_frequency', { label: 'Week Day and Week of Month', value: 'semimonthly-option2' });
          }
        } else if (payFreqCodeKey === 'pay_frequency_monthly') {
          // Determine if it's specific day or week-based
          if (customer.pay_first_day_of_month_dropdown_option_id) {
            setPayFrequencyValueOption('monthly-option1');
            setValue('monthly_pay_frequency', { label: 'Pay Day of Months', value: 'monthly-option1' });
          } else if (customer.pay_first_week_of_month_dropdown_option_id) {
            setPayFrequencyValueOption('monthly-option2');
            setValue('monthly_pay_frequency', { label: 'Week Day and Week of Month', value: 'monthly-option2' });
          }
        }
      }
    }
  }, [customer, payFrequencyOptions, dayOfWeekOptions, dayOfMonthOptions, weekOfMonthOptions, setValue, payFrequencyValue, payFrequencyValueOption, dropdownOptions]);

  const checkEligibility = async (forLoanId) => {
    inFlightLoanId.current = forLoanId;
    setLoading(true);
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${forLoanId}/refi_eligibility_check`);
    try {
      const response = await request.get();
      // Ignore a response that a close or a newer opening has superseded.
      if (inFlightLoanId.current !== forLoanId) return;
      if (response.status === 200 || response.status === 201) {
        setEligibilityResult(response?.data?.data);
        if (response?.data?.data?.is_eligible) {
          setShowForm(true);
          // Only fetch bank accounts if customer is eligible
          if (customerId) {
            getCustomerBankAccounts(forLoanId);
          }
        }
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }));
      }
    } catch (error) {
      if (inFlightLoanId.current !== forLoanId) return;
      const errorMessage = error?.response?.data?.message ?? 'Failed to check eligibility';
      dispatch(setNotification({ type: 'error', body: errorMessage }));
    } finally {
      if (inFlightLoanId.current === forLoanId) setLoading(false);
    }
  };

    const getCustomerBankAccounts = async (forLoanId) => {
    try {
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer/${customerId}/express_loan_info`);
      const response = await request.get();
      if (inFlightLoanId.current !== forLoanId) return;

      if (response.status === 200 || response.status === 201) {
        // Set customer data
        setCustomer(response?.data?.data?.customer);

        const bankAccountOptions = response?.data?.data?.customer_bank_accounts.map((account) => {
          return {
            label: `Routing #: ${account.bank_routing_number}, Account #: ${account.bank_account_number_last_four}`,
            value: account.id
          };
        });

        setCustomerBankAccounts(bankAccountOptions);

        // Find the primary bank account (is_primary_account = 1) and pre-select it
        const primaryAccount = response?.data?.data?.customer_bank_accounts.find((account) => account.is_primary_account === 1);
        if (primaryAccount) {
          const primaryOption = bankAccountOptions.find((option) => option.value === primaryAccount.id);
          setCurrentBankAccount(primaryOption);
        } else if (bankAccountOptions.length > 0) {
          // Fallback to first account if no primary account found
          setCurrentBankAccount(bankAccountOptions[0]);
        }
      }
    } catch (error) {
      if (inFlightLoanId.current !== forLoanId) return;
      const errorMessage = error?.response?.data?.message ?? 'Failed to fetch customer bank accounts';
      dispatch(setNotification({ type: 'error', body: errorMessage }));
    }
  };

  const onSubmit = async (formData) => {
    setSaving(true);
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/loan/${loanId}/cash_out_refinance`);
    try {
      // Initialize the data object with conditional pay frequency fields
      let data = {
        pay_first_day_of_week_dropdown_option_id: null,
        next_pay_date: null,
        pay_first_day_of_month_dropdown_option_id: null,
        pay_second_day_of_month_dropdown_option_id: null,
        pay_first_week_of_month_dropdown_option_id: null,
        pay_second_week_of_month_dropdown_option_id: null,
        pay_second_day_of_week_dropdown_option_id: null,
      };

      // Handle weekly pay frequency
      if (formData.pay_frequency_dropdown_option_id?.value === Object.values(dropdownOptions).find((option) => option?.code_key === 'pay_frequency_weekly')?.id) {
        if (formData.pay_first_day_of_week_dropdown_option_id?.value) {
          data.pay_first_day_of_week_dropdown_option_id = formData.pay_first_day_of_week_dropdown_option_id.value;
        }
      }

      // Handle bi-weekly pay frequency
      if (formData.pay_frequency_dropdown_option_id?.value === Object.values(dropdownOptions).find((option) => option?.code_key === 'pay_frequency_biweekly')?.id) {
        if (formData.next_pay_date) {
          data.next_pay_date = formData.next_pay_date;
        }
      }

      // Handle semi-monthly pay frequency
      if (formData.pay_frequency_dropdown_option_id?.value === Object.values(dropdownOptions).find((option) => option?.code_key === 'pay_frequency_semimonthly')?.id) {
        if (payFrequencyValueOption === "semimonthly-option1") {
          if (formData.pay_first_day_of_month_dropdown_option_id?.value) {
            data.pay_first_day_of_month_dropdown_option_id = formData.pay_first_day_of_month_dropdown_option_id.value;
          }
          if (formData.pay_second_day_of_month_dropdown_option_id?.value) {
            data.pay_second_day_of_month_dropdown_option_id = formData.pay_second_day_of_month_dropdown_option_id.value;
          }
        } else if (payFrequencyValueOption === "semimonthly-option2") {
          if (formData.pay_first_week_of_month_dropdown_option_id?.value) {
            data.pay_first_week_of_month_dropdown_option_id = formData.pay_first_week_of_month_dropdown_option_id.value;
          }
          if (formData.pay_first_day_of_week_dropdown_option_id?.value) {
            data.pay_first_day_of_week_dropdown_option_id = formData.pay_first_day_of_week_dropdown_option_id.value;
          }
          if (formData.pay_second_week_of_month_dropdown_option_id?.value) {
            data.pay_second_week_of_month_dropdown_option_id = formData.pay_second_week_of_month_dropdown_option_id.value;
          }
          if (formData.pay_second_day_of_week_dropdown_option_id?.value) {
            data.pay_second_day_of_week_dropdown_option_id = formData.pay_second_day_of_week_dropdown_option_id.value;
          }
        }
      }

      // Handle monthly pay frequency
      if (formData.pay_frequency_dropdown_option_id?.value === Object.values(dropdownOptions).find((option) => option?.code_key === 'pay_frequency_monthly')?.id) {
        if (payFrequencyValueOption === "monthly-option1") {
          if (formData.pay_first_day_of_month_dropdown_option_id?.value) {
            data.pay_first_day_of_month_dropdown_option_id = formData.pay_first_day_of_month_dropdown_option_id.value;
          }
        } else if (payFrequencyValueOption === "monthly-option2") {
          if (formData.pay_first_week_of_month_dropdown_option_id?.value) {
            data.pay_first_week_of_month_dropdown_option_id = formData.pay_first_week_of_month_dropdown_option_id.value;
          }
          if (formData.pay_first_day_of_week_dropdown_option_id?.value) {
            data.pay_first_day_of_week_dropdown_option_id = formData.pay_first_day_of_week_dropdown_option_id.value;
          }
        }
      }

      // Format the data for the API
      const submitData = {
        customer_id: customerId,
        customer_bank_account_id: formData.customer_bank_account_id?.value,
        pay_frequency_dropdown_option_id: formData.pay_frequency_dropdown_option_id?.value,
        is_cash_out: true,
        current_date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'), // Format as YYYY-MM-DD HH:MM:SS
        ...data // Include conditional pay frequency fields
      };

      const response = await request.post(submitData);
      if (response.status === 200 || response.status === 201) {
        const result = response?.data;
        if (result?.is_success === true) {
          handleClose();
          // Redirect to the same page that "Refinance - No-Cash Out" would
          const pathSegments = window.location.pathname.split('/');
          const moduleName = pathSegments[1];
          setTimeout(() => window.location.href = `/loan_refinance/${customerId}?is_cash_out=true`, 1000);
        } else {
          // Show the underwriting failure message
          setEligibilityResult({
            is_eligible: false,
            reason: 'Unfortunately, you are not eligible for a Cash-Out Refi at this time. You’ll be receiving an email with more information regarding this decision.'
          });
          setShowForm(false);
        }
      } else {
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }));
      }
    } catch (error) {
      if (error?.response?.data?.error_key === 'lead_funnel_denial') {
        setSaving(false);
        resetState();
        dispatch(setModal({ is_active: true, type: 'ds_denied_modal', options: { source: 'cash_out_refinance' } }))
        return;
      }
      const errorMessage = error?.response?.data?.message ?? 'Failed to process cash out refinance';
      dispatch(setNotification({ type: 'error', body: errorMessage }));
    } finally {
      setSaving(false);
    }
  };

  // Clears everything tied to the loan this modal was opened for. The modal stays mounted in the
  // Footer, so any close path that skips this leaks the bank account and pay schedule into the next loan.
  const resetState = () => {
    setEligibilityResult(null);
    setShowForm(false);
    setLoading(true);
    setCustomerBankAccounts([]);
    setCurrentBankAccount(null);
    setCustomer({});
    setPayFrequencyValue("");
    setPayFrequencyValueOption("");
    setSaving(false);
    reset();
  };

  const handleClose = () => {
    resetState();
    dispatch(setModal({ is_active: false }));
  };

    const renderEligibilityResult = () => {
    if (eligibilityResult?.is_eligible) {
      return (
        <div className="notification is-success is-light">
          <h3 className="title is-size-5 mb-1">
            Initial Check Passed
          </h3>
          <p className="is-size-6">
            This loan is eligible for cash out refinance.
          </p>
        </div>
      );
    } else {
      return (
        <div className="notification is-danger is-light">
          <h3 className="title is-size-5 mb-1">
            Not Eligible
          </h3>
          <p className="is-size-6">
            {eligibilityResult?.reason || 'This customer is not eligible for a cash out refi. To get additional principal, they would need to pay off their existing loan and apply for a new one.'}
          </p>
        </div>
      );
    }
  };

  const renderPayFrequencyFields = () => {
    return (
      <>
        <div className="column">
          <div className="field">
            <label htmlFor="pay_frequency_dropdown_option_id">
              Pay Frequency
              <span className="has-text-danger">*</span>
            </label>
            <Controller
              name="pay_frequency_dropdown_option_id"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <Select
                  {...field}
                  name="pay_frequency_dropdown_option_id"
                  classNames={reactSelectClassNames()}
                  options={payFrequencyOptions}
                  isSearchable={false}
                  onChange={(val) => {
                    field.onChange(val);
                    setPayFrequencyValue(val?.label);
                  }}
                />
              )}
            />
            <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
              {errors.pay_frequency_dropdown_option_id && <span>{errors.pay_frequency_dropdown_option_id.message ? errors.pay_frequency_dropdown_option_id.message : "Pay Frequency is required"}</span>}
            </p>
          </div>
        </div>

        {/* display this option if semi-monthly option is selected */}
        <div className={`column ${payFrequencyValue === "Semi-Monthly" ? "" : "is-hidden"}`}>
          <div className="field">
            <label htmlFor="semi_monthly_pay_frequency">
              Semi Monthly Option:
            </label>
            <Controller
              name="semi_monthly_pay_frequency"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  name="semi_monthly_pay_frequency"
                  classNames={reactSelectClassNames()}
                  options={[{
                    label: 'Specific Days',
                    value: 'semimonthly-option1'
                  }, { label: 'Week Day and Week of Month', value: 'semimonthly-option2' }]}
                  isSearchable={false}
                  onChange={(val) => {
                    field.onChange(val);
                    setPayFrequencyValueOption(val?.value);
                  }}
                />
              )}
            />
          </div>
        </div>

        {/* display this option if monthly option is selected */}
        <div className={`column ${payFrequencyValue === "Monthly" ? "" : "is-hidden"}`}>
          <div className="field">
            <label htmlFor="monthly_pay_frequency">
              Monthly Option:
            </label>
            <Controller
              name="monthly_pay_frequency"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  name="monthly_pay_frequency"
                  classNames={reactSelectClassNames()}
                  options={[{
                    label: 'Pay Day of Months',
                    value: 'monthly-option1'
                  }, { label: 'Week Day and Week of Month', value: 'monthly-option2' }]}
                  isSearchable={false}
                  onChange={(val) => {
                    field.onChange(val);
                    setPayFrequencyValueOption(val?.value);
                  }}
                />
              )}
            />
          </div>
        </div>

        {/* display this option if Semi-Monthly and Week Day and Week of Month choosen */}
        {/* display this option if Monthly and Week Day and Week of Month choosen */}
        <div className={`column ${((payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option2") || (payFrequencyValue === "Monthly" && payFrequencyValueOption === "monthly-option2")) ? "" : "is-hidden"}`}>
          <div className="field">
            <label htmlFor="pay_first_week_of_month_dropdown_option_id">
              {payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option2" && (<>Week 1:</>)}
              {payFrequencyValue === "Monthly" && payFrequencyValueOption === "monthly-option2" && (<>Week:</>)}
            </label>
            <Controller
              name="pay_first_week_of_month_dropdown_option_id"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  name="pay_first_week_of_month_dropdown_option_id"
                  classNames={reactSelectClassNames()}
                  options={weekOfMonthOptions}
                  isSearchable={false}
                  onChange={(val) => field.onChange(val)}
                />
              )}
            />
          </div>
        </div>

        {/* display this option if weekly choosen */}
        {/* display this option if semi-monthly and week day and week month choosen */}
        {/* display this option if Monthly and Week Day and Week of Month choosen */}
        <div className={`column ${(payFrequencyValue === "Weekly" || (payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option2") || (payFrequencyValue === "Monthly" && payFrequencyValueOption === "monthly-option2")) ? "" : "is-hidden"}`}>
          <div className="field">
            <label htmlFor="pay_first_day_of_week_dropdown_option_id">
              {payFrequencyValue === "Weekly" && (<>Pay day of week:</>)}
              {payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option2" && (<>Day 1:</>)}
              {payFrequencyValue === "Monthly" && payFrequencyValueOption === "monthly-option2" && (<>Day:</>)}
            </label>
            <Controller
              name="pay_first_day_of_week_dropdown_option_id"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  name="pay_first_day_of_week_dropdown_option_id"
                  classNames={reactSelectClassNames()}
                  options={dayOfWeekOptions}
                  isSearchable={false}
                  menuPosition="fixed"
                  menuPortalTarget={modalRef.current}
                  onChange={(val) => field.onChange(val)}
                />
              )}
            />
          </div>
        </div>

        {/* display this option if Semi-Monthly and Week Day and Week of Month choosen */}
        <div className={`column ${(payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option2") ? "" : "is-hidden"}`}>
          <div className="field">
            <label htmlFor="pay_second_week_of_month_dropdown_option_id">
              Week 2:
            </label>
            <Controller
              name="pay_second_week_of_month_dropdown_option_id"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  name="pay_second_week_of_month_dropdown_option_id"
                  classNames={reactSelectClassNames()}
                  options={weekOfMonthOptions}
                  isSearchable={false}
                  onChange={(val) => field.onChange(val)}
                />
              )}
            />
          </div>
        </div>

        {/* display this option if Semi-Monthly and Week Day and Week of Month choosen */}
        <div className={`column ${(payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option2") ? "" : "is-hidden"}`}>
          <div className="field">
            <label htmlFor="pay_second_day_of_week_dropdown_option_id">
              Day 2:
            </label>
            <Controller
              name="pay_second_day_of_week_dropdown_option_id"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  name="pay_second_day_of_week_dropdown_option_id"
                  classNames={reactSelectClassNames()}
                  options={dayOfWeekOptions}
                  isSearchable={false}
                  onChange={(val) => field.onChange(val)}
                />
              )}
            />
          </div>
        </div>

        {/* display this option if bi-weekly choosen */}
        <div className={`column ${payFrequencyValue === "Bi-Weekly" ? "" : "is-hidden"}`}>
          <div className="field">
            <label htmlFor="next_pay_date">
              Next Pay Date:
            </label>
            <Controller
              name="next_pay_date"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  className="input is-small"
                  type="date"
                  value={field.value || ""}
                  onChange={(val) => field.onChange(val)}
                />
              )}
            />
          </div>
        </div>

        {/* display this option if Semi-Monthly and Specific Days choosen */}
        {/* display this option if Monthly and Pay Day of Months choosen */}
        <div className={`column ${((payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option1") || (payFrequencyValue === "Monthly" && payFrequencyValueOption === "monthly-option1")) ? "" : "is-hidden"}`}>
          <div className="field">
            <label htmlFor="pay_first_day_of_month_dropdown_option_id">
              {payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option1" && (<>First payment day of month:</>)}
              {payFrequencyValue === "Monthly" && payFrequencyValueOption === "monthly-option1" && (<>Day:</>)}
            </label>
            <Controller
              name="pay_first_day_of_month_dropdown_option_id"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  name="pay_first_day_of_month_dropdown_option_id"
                  classNames={reactSelectClassNames()}
                  options={dayOfMonthOptions}
                  isSearchable={false}
                  onChange={(val) => field.onChange(val)}
                />
              )}
            />
          </div>
        </div>

        {/* display this option if Semi-Monthly and Specific Days choosen */}
        <div className={`column ${(payFrequencyValue === "Semi-Monthly" && payFrequencyValueOption === "semimonthly-option1") ? "" : "is-hidden"}`}>
          <div className="field">
            <label htmlFor="pay_second_day_of_month_dropdown_option_id">
              Second payment day of month:
            </label>
            <Controller
              name="pay_second_day_of_month_dropdown_option_id"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  name="pay_second_day_of_month_dropdown_option_id"
                  classNames={reactSelectClassNames()}
                  options={dayOfMonthOptions}
                  isSearchable={false}
                  onChange={(val) => field.onChange(val)}
                />
              )}
            />
          </div>
        </div>
      </>
    );
  };

  const renderForm = () => {
    return (
      <>
        <div className="columns mb-5 pb-5 is-flex-direction-column">
          <div className="column mb-3">
            <h3>
              Use this action when a customer has an active loan and is interested in borrowing additional principal. This will perform an initial check to see if the customer can continue with the process.
            </h3>
          </div>
        </div>

        <div className="columns mb-0 is-flex-direction-column">
          <div className="column">
            <div className="field">
              <label htmlFor="customer_bank_account_id">
                Customer Bank Account
                <span className="has-text-danger">*</span>
              </label>
              <Controller
                name="customer_bank_account_id"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select
                    {...field}
                    name="customer_bank_account_id"
                    classNames={reactSelectClassNames()}
                    options={customerBankAccounts}
                    isSearchable={false}
                    onChange={(val) => {
                      field.onChange(val);
                    }}
                  />
                )}
              />
              <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                {errors.customer_bank_account_id && <span>{errors.customer_bank_account_id.message ? errors.customer_bank_account_id.message : "Customer Bank Account is required"}</span>}
              </p>
            </div>
          </div>

          {renderPayFrequencyFields()}
        </div>

        <SpecialInstruction
          content={"<span>" +
            "<span class='has-text-weight-bold'>This will perform the following actions:</span>" + "<br>" +
            "<span class='pl-2'>• Check Customer Details Against Exclusion List (OEL)</span> <br> " +
            "<span class='pl-2'>• Run a check for MLA and OFAC</span> <br> " +
            "<span class='pl-2'>• Create a new Lead for this customer</span> <br> " +
            "<span class='pl-2'>• Validate and update loan terms based on new information</span> <br> " +
            " </span>"}
          type="is-warning"
        />
      </>
    );
  };

  return modal.is_active && modal.type === 'refinance_cash_out_modal' ? (
    <form onSubmit={handleSubmit(onSubmit)} id="refinance_cash_out_modal_form" className="lox-form">
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-card modal-card-confirmation column is-5" ref={modalRef}>
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized">
              Cash Out Refi Eligibility Check
            </p>
            <button className="delete" aria-label="close" onClick={handleClose}></button>
          </header>
          <section
            className="modal-card-body modal-card-body pt-1 pb-1"
            style={{ minHeight: "100px", overflow: 'hidden' }}
          >
            {loading && (
              <div className="has-text-centered py-4">
                <Loading />
                <p className="mt-2">Checking eligibility...</p>
              </div>
            )}
            {!loading && showForm && renderForm()}
            {!loading && !showForm && renderEligibilityResult()}
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1 pr-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button
                type="button"
                className="button is-small has-background-grey has-text-white"
                onClick={handleClose}
                disabled={showForm && (loading || customerBankAccounts.length === 0 || payFrequencyOptions.length === 0)}
              >
                Close
              </button>
              {showForm && (
                <button
                  type="submit"
                  className="button is-link is-small ml-2"
                  disabled={saving || loading || customerBankAccounts.length === 0 || payFrequencyOptions.length === 0}
                >
                  {saving ? 'Processing...' : 'Submit'}
                </button>
              )}
            </div>
          </footer>
        </div>
      </div>
    </form>
  ) : null;
}
